import { useNavigate } from 'react-router-dom'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BulkMarkAsPaidModal } from '../bulkMarkAsPaidModal/BulkMarkAsPaidModal'
import { CreateDisbursementModal } from '../createDisbursementModal/CreateDisbursementModal'
import { getDisbursementBatchType } from '../disbursementDetails/DisbursementFoundationDetails/DisbursementFoundationDetails'

import {
  Badge,
  Button,
  Flex,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters
} from '@percent/lemonade'
import { ErrorView, FoundationNameWithFlag } from '@percent/admin-dashboard/common/components'
import { useFoundations, usePermissions } from '@percent/admin-dashboard/common/hooks'
import {
  usePostHogFeatureFlag,
  POSTHOG_FEATURE_FLAGS
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { DisbursementsTableProps } from './DisbursementsTable.types'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { DisbursementsFilters } from './disbursementsFilters/DisbursementsFilters'
import { DisbursementsFilterChips } from './disbursementsFilters/DisbursementsFilterChips'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import { useDisbursementSearch } from './useDisbursementSearch'
import {
  DisbursementBatchesSortDirection,
  isDisbursementBatchesSortDirection,
  isDisbursementBatchesSortField
} from '@percent/admin-dashboard/api/actions/disbursements/disbursements.types'

const formatBatchDate = (date: string) => dayJS(date).format('DD MMM YYYY')

export function DisbursementsTable({
  isLoading,
  totalResults,
  data,
  previousPage,
  nextPage,
  refreshToFirstPage,
  errorMessage,
  queryParams,
  setQueryParams
}: DisbursementsTableProps) {
  const { t } = useTranslation()
  const { foundations } = useFoundations()
  const navigate = useNavigate()
  const { userHasPermissions } = usePermissions()
  const [isCreateDisbursementModalOpen, setIsCreateDisbursementModalOpen] = useState(false)
  const [isBulkMarkAsPaidModalOpen, setIsBulkMarkAsPaidModalOpen] = useState(false)

  const internalFgaDoraDisbursementsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_DORA_DISBURSEMENTS
  )

  const canCreateBatch = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_create')
  const canMarkAsPaid = !internalFgaDoraDisbursementsFeatureFlag || userHasPermissions('disbursements:can_mark_paid')

  const columns: TableColumn[] = useMemo(
    () => [
      { id: 'name', content: t('table.name'), isSortable: false, props: { width: '288px' } },
      { id: 'foundation', content: t('table.foundation'), isSortable: false, props: { width: '288px' } },
      { id: 'paidBy', content: t('table.paidBy'), isSortable: false, props: { width: '160px' } },
      { id: 'createdAt', content: t('table.createdDate'), isSortable: true, props: { width: '200px' } },
      { id: 'endDate', content: t('table.upTo'), isSortable: true, props: { width: '200px' } }
    ],
    [t]
  )

  const search = useDisbursementSearch({
    search: queryParams.search,
    onSearch: value => setQueryParams({ ...queryParams, search: value })
  })

  // Narrowed rather than passed through: both come from the URL, and the endpoint's sort enum is
  // strict, so an unrecognised value would 400 instead of falling back.
  const activeSort = isDisbursementBatchesSortField(queryParams.sort) ? queryParams.sort : undefined
  const activeDirection = isDisbursementBatchesSortDirection(queryParams.direction)
    ? queryParams.direction
    : DisbursementBatchesSortDirection.DESC

  const handleRequestSort = (columnKey: string) => {
    if (!isDisbursementBatchesSortField(columnKey)) {
      return
    }

    const isSameColumn = activeSort === columnKey

    setQueryParams({
      ...queryParams,
      sort: columnKey,
      direction:
        isSameColumn && activeDirection === DisbursementBatchesSortDirection.ASC
          ? DisbursementBatchesSortDirection.DESC
          : DisbursementBatchesSortDirection.ASC
    })
  }

  const displayTotalResults = totalResults ?? 0

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={displayTotalResults}
        isLoading={isLoading}
        illustration="no-results"
        emptyTableTitle={t('table.emptyDisbursements')}
        // Not "disbursementsTable" — the batch details page already uses that for the table of
        // disbursements inside a batch. This one lists the batches themselves.
        dataTestId="disbursementBatchesTable"
        sortByColumn={activeSort}
        sortDirection={activeDirection}
        handleRequestSort={handleRequestSort}
      >
        <TableSearchAndFilters>
          {/* The design grows the search box into whatever the filters and buttons leave, so this
              flexes rather than taking a fixed width. Siblings below are shrink-0 so they keep
              their intrinsic size instead of competing for it.
              TableSearchAndFilters already gaps its children by 8px; the design wants 12. */}
          <Flex flex="1 0 0" miw={0} mr={4}>
            <SearchInput
              name="disbursementSearch"
              value={search.value}
              onChange={search.onChange}
              onKeyDown={search.onKeyDown}
              onBlur={search.onBlur}
              handleClearValue={search.handleClearValue}
              placeholder={t('typography.searchByDisbursementNameOrId')}
              data-testid="disbursements-search-input"
              status="default"
              inputSize="small"
            />
          </Flex>
          <DisbursementsFilters queryParams={queryParams} setQueryParams={setQueryParams} />
          <Flex flex="0 0 auto" align="center" gap={12} ml={4}>
            {canMarkAsPaid && (
              <Button onClick={() => setIsBulkMarkAsPaidModalOpen(true)} size="small">
                {t('button.bulkMarkAsPaid')}
              </Button>
            )}
            <Button onClick={() => setIsCreateDisbursementModalOpen(true)} size="small" disabled={!canCreateBatch}>
              {t('button.createBatch')}
            </Button>
          </Flex>
        </TableSearchAndFilters>

        <DisbursementsFilterChips queryParams={queryParams} setQueryParams={setQueryParams} />

        {/* The error replaces the rows, not the page: a filter value the strict endpoint rejects is
            one of the ways to get here, and unmounting the filter row would take away the only
            controls that can clear it. */}
        {errorMessage ? (
          <ErrorView errorMessage={errorMessage} />
        ) : (
          <>
            <Table>
              <TableBody>
                {data?.map(row => (
                  <TableRow key={row.id} onClick={handleClickAsLink(`/disbursements/${row.id}`, navigate)}>
                    <TableCell title={row.name}>{row.name}</TableCell>
                    <TableCell>
                      {foundations?.[row.foundationId] ? (
                        <FoundationNameWithFlag
                          countryCode={foundations[row.foundationId].countryCode}
                          name={foundations[row.foundationId].name}
                        />
                      ) : (
                        row.foundationId
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Empty until the batch has disbursements, and an empty badge is a stray pill. */}
                      {getDisbursementBatchType(row.detailedCounts) ? (
                        <Badge variant="default">{getDisbursementBatchType(row.detailedCounts)}</Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>{formatBatchDate(row.createdAt)}</TableCell>
                    <TableCell>{formatBatchDate(row.endDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              totalResultsText={t('table.results', { count: displayTotalResults })}
              previousPage={previousPage}
              nextPage={nextPage}
            />
          </>
        )}
      </TableProvider>
      <CreateDisbursementModal
        openModal={isCreateDisbursementModalOpen}
        onClose={() => setIsCreateDisbursementModalOpen(false)}
        refreshDisbursementsTable={refreshToFirstPage}
      />
      {isBulkMarkAsPaidModalOpen && (
        <BulkMarkAsPaidModal onClose={() => setIsBulkMarkAsPaidModalOpen(false)} refresh={refreshToFirstPage} />
      )}
    </>
  )
}
